import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  List,
  ListItem,
  Radio,
  RadioGroup,
  TextField
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Unstable_Grid2";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm
} from "react-hook-form";

import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import {
  UserNotifications,
  useUpdateUser,
  useUpdateUserEmail,
  useUserById
} from "./userUtils";
import {
  ProgramMembership,
  ProgramRole,
  UpdateUserEmailArgs,
  UpdateUserInput,
  User
} from "../../API";
import { useUpdateMemberships } from "../Memberships/membershipUtils";
import {
  PGYSelectFields,
  ProgramRoleSelectFields,
  Regex,
  TraineeTypeSelectFields,
  UserFormErrors
} from "../../app/constants";
import { LoadingButton } from "@mui/lab";
import { ReactHookFormTextField } from "../../common-ui/ReactHookFormTextField";
import {
  ReactHookFormSelectField,
  SelectMenuOption
} from "../../common-ui/ReactHookFormSelectField";
import { format } from "date-fns";
import { getProgramsCurrentUserCanAdminister } from "../../app/utils";
import { useDispatch } from "react-redux";
import {
  setShowToast,
  setToastMessage,
  setToastStyle
} from "../../common-ui/toastSlice";
import { generateUUID } from "../../mocks/dataGenerators";
import { useAppSelector } from "../../app/hooks";
import {
  selectPrograms,
  selectProgramsStatus
} from "../Programs/programsSlice";

const pageDetails: PageWrapperProps = {
  className: "EditUser"
};

type NewProgramRole = ProgramRole | "Clinician";

// Stripping away nested objects to avoid circular references for react-hook-form
// Also ridding fields not necessary for display in the form.
export type MembershipPreview = Omit<
  ProgramMembership,
  "programMember" | "program" | "createdAt" | "updatedAt"
> & {
  isNew?: boolean;
  institutionId?: string;
  programRole: NewProgramRole;
  isClinicianTrainee?: boolean | undefined; // Radio state when role = Clinician
};

export type EditUserInputs = {
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  npi: number | null;
  pgy: number | null;
  simplLearnId: string | null;
  suffix: string | null;
  programMemberships: MembershipPreview[];
  jobTitle?: string | null;
};

type EditUserProps = {
  id: string;
};

export const EditUser = ({ id }: EditUserProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user, status, error, getUserById } = useUserById(id);
  const {
    status: updateUserStatus,
    error: updateUserError,
    updateUser
  } = useUpdateUser();
  const [indexOfMembershipToEnd, setIndexOfMembershipToEnd] =
    useState<number>();
  const [showEndMembershipModal, setShowEndMembershipModal] = useState(false);
  const {
    status: updateEmailStatus,
    error: updateEmailError,
    updateUserEmail
  } = useUpdateUserEmail();
  const {
    status: updateMembershipsStatus,
    error: updateMembershipsError,
    updateMemberships
  } = useUpdateMemberships();

  const {
    programs: programsCurrentUserCanAdminister,
    institutions: institutionsCurrentUserCanAdminister
  } = getProgramsCurrentUserCanAdminister();

  const getProgramsInInstitution = (institutionId?: string) =>
    programsCurrentUserCanAdminister?.filter(
      (p) => p.institutionId == institutionId
    );

  const programs = useAppSelector(selectPrograms);
  const programsStatus = useAppSelector(selectProgramsStatus);

  const {
    getValues,
    handleSubmit,
    reset,
    trigger,
    setError,
    clearErrors,
    setValue,
    watch,
    control,
    formState: { errors, isDirty, dirtyFields }
  } = useForm<EditUserInputs>({
    defaultValues: {
      programMemberships: []
    },
    reValidateMode: "onChange"
  });

  const {
    fields: membershipFields,
    append: appendMembership,
    remove: removeMembership,
    replace: replaceMemberships
  } = useFieldArray({
    name: "programMemberships",
    control,
    rules: {
      validate: {
        unique: (programMemberships) => {
          let twoMembershipsWithSameProgram = false;
          const activeMemberships = programMemberships.filter(
            (programMembership) => !programMembership.endDate
          );
          activeMemberships?.forEach((activeMembership, index) => {
            twoMembershipsWithSameProgram = activeMemberships.some(
              (innerValue, innerIndex) =>
                index !== innerIndex &&
                activeMembership.programId === innerValue.programId &&
                activeMembership.programRole === innerValue.programRole
            );
          });
          return (
            !twoMembershipsWithSameProgram ||
            UserFormErrors.DuplicateProgramMembership
          );
        },
        oneTraineeMembership: (programMemberships) => {
          const activeTraineeMembershipCount = programMemberships.filter(
            (membership) => !membership.endDate && membership.isClinicianTrainee
          ).length;
          return (
            activeTraineeMembershipCount <= 1 ||
            UserFormErrors.OneTraineeMembership
          );
        },
        traineeTypeRequired: (programMemberships) => {
          const isTraineeTypeEmpty = (traineeType: any) => {
            return (
              !traineeType ||
              traineeType === "" ||
              traineeType === null ||
              traineeType === undefined
            );
          };

          const missingTraineeType = programMemberships.find(
            (membership) =>
              !membership.endDate &&
              membership.isNew &&
              membership.isClinicianTrainee === true &&
              isTraineeTypeEmpty(membership.traineeType)
          );
          return !missingTraineeType || "Trainee Type is required for trainees";
        },
        traineeCannotBePA: (programMemberships) => {
          const activeRoles = programMemberships
            .filter((m) => !m.endDate)
            .map((m) =>
              m.isClinicianTrainee ? ProgramRole.Trainee : m.programRole
            );
          const hasTrainee = activeRoles.includes(ProgramRole.Trainee);
          const hasProgramAdmin = activeRoles.includes(
            ProgramRole.ProgramAdmin
          );

          return (
            !(hasTrainee && hasProgramAdmin) ||
            UserFormErrors.TraineeCannotBeProgramAdmin
          );
        }
      }
    }
  });

  watch("programMemberships");
  
  const transformMemberships = (
    programMemberships: MembershipPreview[]
  ): ProgramMembership[] => {
    return programMemberships.map((m) => {
      const role = m.programRole as NewProgramRole; // <-- key change

      let finalRole: ProgramRole;
      if (role === "Clinician") {
        finalRole = m.isClinicianTrainee
          ? ProgramRole.Trainee
          : ProgramRole.Attending;
      } else {
        finalRole = role; // Safe because it's already a ProgramRole
      }

      const { isNew, institutionId, isClinicianTrainee, ...rest } = m;

      return {
        ...rest,
        programRole: finalRole
      } as ProgramMembership;
    });
  };

  const onSubmit: SubmitHandler<EditUserInputs> = async (data) => {
    if (id) {
      const { email, npi, programMemberships } = data;
      if (editingEmail) {
        if (email?.toLowerCase() === user?.email.toLowerCase()) {
          setError("email", {
            type: "custom",
            message: UserFormErrors.RequiresUniqueEmail
          });
        } else {
          const updateUserEmailInput: UpdateUserEmailArgs = {
            userId: user?.id,
            newEmail: email
          };
          const result = await updateUserEmail(updateUserEmailInput);
          if (result) {
            // Success: show toast, exit edit modes, and refresh user to reflect saved email
            dispatch(setToastMessage(UserNotifications.EmailUpdatedSuccessfully));
            dispatch(setToastStyle("success"));
            dispatch(setShowToast(true));
            setEditingEmail(false);
            setEditingUser(false);
            if (id) {
              getUserById(id);
            }
          }
        }
      } else {
        // Require NPI when user has an active role that requires it (Attending or Trainee/Clinician)
        const rolesRequiringNPI = [ProgramRole.Attending, ProgramRole.Trainee];
        const activeMemberships = (programMemberships || []).filter((m) => !m.endDate);
        const hasRoleRequiringNPI = activeMemberships.some((m) => {
          const role = m.programRole as NewProgramRole;
          if (role === "Clinician") {
            // Clinician maps to Attending or Trainee; both require NPI
            return true;
          }
          return rolesRequiringNPI.includes(role as ProgramRole);
        });
        if (hasRoleRequiringNPI && !npi) {
          setError("npi", {
            type: "custom",
            message: UserFormErrors.NPIRequired
          });
          return;
        }

        // 1) Update user first; if this fails (e.g., NPI conflict), do not save memberships
        const updateUserInput: UpdateUserInput = {
          id,
          firstName: data.firstName,
          lastName: data.lastName,
          npi: data.npi,
          pgy: data.pgy || null,
          simplLearnId: data.simplLearnId,
          suffix: data.suffix,
          jobTitle: data.jobTitle
        };
        const updatedUserResult = await updateUser(updateUserInput);
        if (!updatedUserResult) {
          // Inline NPI error will be set by existing effect; keep edit mode, do not save memberships
          return;
        }

        // 2) With user updated, update memberships if applicable
        if (user?.eulaAcceptedDate) {
          const transformed =
            transformMemberships(programMemberships) as unknown as ProgramMembership[];
          const membershipResult = await updateMemberships(id, transformed);
          if (!membershipResult || !membershipResult.success) {
            // Show toast and inline error
            dispatch(setToastMessage(updateMembershipsError || "Error updating memberships"));
            dispatch(setToastStyle("error"));
            dispatch(setShowToast(true));
            setError("programMemberships", {
              type: "server",
              message: updateMembershipsError || "Error updating memberships"
            });
            return; // keep edit mode to correct memberships
          }
        }

        // 3) All done; close edit mode and refresh state
        setEditingUser(false);
        dispatch(setToastMessage(UserNotifications.UserUpdatedSuccessfully));
        dispatch(setToastStyle("success"));
        dispatch(setShowToast(true));
        if (id) {
          getUserById(id);
        }
      }
    }
  };

  const promptEndMembership = (index: number) => {
    setIndexOfMembershipToEnd(index);
    setShowEndMembershipModal(true);
  };

  const setFormToStoredValues = (user: User) => {
    const {
      email,
      firstName,
      lastName,
      npi,
      pgy,
      simplLearnId,
      programMemberships,
      eulaAcceptedDate,
      suffix,
      jobTitle
    } = user;
    setRegistrationDate(
      eulaAcceptedDate ? format(new Date(eulaAcceptedDate), `PPPPpppp`) : "N/A"
    );
    // Need to revisit when a user can be archived and/or deleted.
    setUserStatus(eulaAcceptedDate ? "Active" : "Unregistered");
    let resolvedJobTitle = jobTitle;
    if (!resolvedJobTitle && Array.isArray(programMemberships?.items)) {
      const activeRoles = programMemberships?.items
        .filter(
          (pm) =>
            !pm?.endDate &&
            (pm?.programRole === ProgramRole.Trainee ||
              pm?.programRole === ProgramRole.Attending)
        )
        .map((pm) => pm?.programRole);

      if (activeRoles?.includes(ProgramRole.Trainee)) {
        resolvedJobTitle = "Trainee";
      } else if (activeRoles?.includes(ProgramRole.Attending)) {
        resolvedJobTitle = "Attending";
      } else {
        resolvedJobTitle = null;
      }
    }
    const flatMappedProgramMemberships = programMemberships?.items?.flatMap(
      (programMembership) => {
        if (!programMembership) return [];

        const transformedRole =
          programMembership.programRole === ProgramRole.Trainee ||
          programMembership.programRole === ProgramRole.Attending
            ? "Clinician"
            : programMembership.programRole;

        const isClinicianTrainee =
          programMembership.programRole === ProgramRole.Trainee;

        return [
          {
            ...programMembership,
            institutionId: programMembership.program?.institutionId,
            programRole: transformedRole,
            isClinicianTrainee
          }
        ];
      }
    ) as MembershipPreview[] || [];
    // Reset the entire form including the field array so isDirty is false on load
    reset(
      {
        email,
        firstName,
        lastName,
        npi,
        pgy,
        simplLearnId,
        suffix,
        jobTitle: resolvedJobTitle,
        programMemberships: flatMappedProgramMemberships
      },
      {
        keepDirty: false,
        keepTouched: false,
        keepErrors: false
      }
    );
    setFormPopulated(true);
  };

  const [formPopulated, setFormPopulated] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [userStatus, setUserStatus] = useState("");
  const [registrationDate, setRegistrationDate] = useState("");
  const [editingUser, setEditingUser] = useState(false);
  const [initialDataLoadComplete, setInitialDataLoadComplete] = useState(false);
  // True if any non-email field is dirty (supports nested dirtyFields)
  const hasNonEmailDirty = React.useMemo(() => {
    if (!isDirty) return false;
    const hasAnyNonEmailDirty = (df: Record<string, any>): boolean =>
      Object.entries(df || {}).some(([key, val]) => {
        if (key === "email") return false;
        if (val === true) return true;
        if (val && typeof val === "object") return hasAnyNonEmailDirty(val);
        return false;
      });
    return hasAnyNonEmailDirty(dirtyFields as unknown as Record<string, any>);
  }, [isDirty, dirtyFields]);

  const anyApiIsLoading = [
    status,
    updateUserStatus,
    updateMembershipsStatus
  ].includes("loading");
  const statusOfRequiredInitialData = [status, programsStatus];
  const loadingRequiredInitialData =
    statusOfRequiredInitialData.includes("loading");
  const errorLoadingRequiredInitialData =
    statusOfRequiredInitialData.includes("failed");
  const loadedRequiredInitialData = statusOfRequiredInitialData.every(
    (status) => status === "succeeded"
  );

  const watchedNpi = watch("npi");

  // Success handling is done inline in onSubmit after both updates succeed
  // If the email update failed on the backend (e.g., 409 conflict), surface it on the email field
  useEffect(() => {
    if (updateEmailStatus === "failed" && updateEmailError) {
      setError("email", {
        type: "server",
        message: updateEmailError
      });
      // Also show a toast for visibility
      dispatch(setToastMessage(updateEmailError));
      dispatch(setToastStyle("error"));
      dispatch(setShowToast(true));
    }
  }, [updateEmailStatus, updateEmailError, setError]);

  useEffect(() => {
    if (updateUserStatus === "failed" && updateUserError) {
      const message = updateUserError;
      if (message.toLowerCase().includes("npi")) {
        setError("npi", {
          type: "server",
          message
        });
      }
      // Always show a toast for failed user update (including NPI conflicts)
      dispatch(setToastMessage(message));
      dispatch(setToastStyle("error"));
      dispatch(setShowToast(true));
    }
  }, [
    updateUserStatus,
    updateUserError,
    dispatch,
    setError
  ]);

  // No separate membership error loop; onSubmit surfaces error inline and returns
  useEffect(() => {
    if (status === "succeeded" && user) {
      setFormToStoredValues(user);
    }
  }, [status, user]);

  useEffect(() => {
    if (loadedRequiredInitialData) setInitialDataLoadComplete(true);
  }, [loadedRequiredInitialData]);

  if (status === "idle") {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Awaiting data...")}</h2>
      </PageWrapper>
    );
  }

  if (loadingRequiredInitialData && !initialDataLoadComplete) {
    return (
      <PageWrapper {...pageDetails}>
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </PageWrapper>
    );
  }

  if (errorLoadingRequiredInitialData) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Error fetching user!")}</h2>
        <p>{error}</p>
      </PageWrapper>
    );
  }

  if (loadedRequiredInitialData && !user) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("User not found!")}</h2>
      </PageWrapper>
    );
  }

  // Dev Note: Need to account for this, especially for testing!
  if (loadedRequiredInitialData && user && !formPopulated) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Populating form...")}</h2>
      </PageWrapper>
    );
  }

  const membershipType: ProgramRole =
    membershipFields.reduce<ProgramRole>((role, mf) => {
      if (mf.programRole == ProgramRole.Admin) {
        return ProgramRole.Admin;
      } else if (
        mf.programRole == ProgramRole.ProgramAdmin ||
        mf.programRole == ProgramRole.Attending
      ) {
        return ProgramRole.ProgramAdmin;
      } else {
        return ProgramRole.Trainee;
      }
    }, ProgramRole.Admin) ?? ProgramRole.Admin;

  const getProgramRoleSelectGroups = (membershipType: ProgramRole) => {
    if (membershipType == ProgramRole.Admin.toString()) {
      return ProgramRoleSelectFields.filter(
        (f) => f.value != ProgramRole.Admin
      );
    }
    return ProgramRoleSelectFields;
  };

  const programRoleSelectFields: { label: string; value: NewProgramRole }[] =
    getProgramRoleSelectGroups(membershipType) as {
      label: string;
      value: NewProgramRole;
    }[];

  if (user && programs && formPopulated) {
    return (
      <PageWrapper {...pageDetails} noTopPadding>
        <Dialog
          open={showEndMembershipModal}
          aria-labelledby="end-membership-title"
          aria-describedby="end-membership-description"
        >
          <DialogTitle id="end-membership-title">
            {t("End Program Membership")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="end-membership-description">
              {t(
                "Are you sure you want to end this program membership? The program end date will be today's date. This User will no longer be able to create or complete assessments in association with this program. They will also no longer show up in the User list of this program. This action cannot be undone."
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowEndMembershipModal(false);
                setIndexOfMembershipToEnd(undefined);
              }}
            >
              {t("Cancel")}
            </Button>
            <Button
              disabled={indexOfMembershipToEnd === undefined}
              onClick={() => {
                if (indexOfMembershipToEnd !== undefined) {
                  const currentValues = getValues("programMemberships");
                  const entryInMembershipFields =
                    currentValues[indexOfMembershipToEnd];
                  const updatedValues = currentValues;
                  updatedValues[indexOfMembershipToEnd] = {
                    ...entryInMembershipFields,
                    endDate: new Date().toISOString()
                  };
                  replaceMemberships(updatedValues);
                  setShowEndMembershipModal(false);
                }
              }}
            >
              {t("End Membership")}
            </Button>
          </DialogActions>
        </Dialog>
        <form
          className="EditUserForm"
          id="edit-user-form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0
          }}
        >
          <Grid
            container
            spacing={2}
            p={1}
            sx={{ flex: 1, minHeight: 0, overflowY: "scroll" }}
          >
            <Grid xs={12}>
              <fieldset>
                <legend>{t("Basic User Info")}</legend>
                <Grid xs={12} container p={1}>
                  <Grid xs={12} md={6}>
                    <ReactHookFormTextField
                      control={control}
                      fieldName="firstName"
                      rules={{ required: UserFormErrors.EmptyFirstName }}
                      label="First Name"
                      required={editingUser}
                      disabled={anyApiIsLoading || editingEmail || !editingUser}
                    />
                    {errors.firstName && (
                      <Alert severity="error">
                        {t(UserFormErrors.EmptyFirstName)}
                      </Alert>
                    )}
                  </Grid>
                  <Grid xs={12} md={6}>
                    <ReactHookFormTextField
                      control={control}
                      fieldName="lastName"
                      rules={{ required: UserFormErrors.EmptyLastName }}
                      label="Last Name"
                      required={editingUser}
                      disabled={anyApiIsLoading || editingEmail || !editingUser}
                    />
                    {errors.lastName && (
                      <Alert severity="error">
                        {t(UserFormErrors.EmptyLastName)}
                      </Alert>
                    )}
                  </Grid>
                  <Grid xs={12} md={6}>
                    <ReactHookFormTextField
                      control={control}
                      fieldName="suffix"
                      label="Suffix"
                      disabled={anyApiIsLoading || editingEmail || !editingUser}
                    />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <ReactHookFormTextField
                      control={control}
                      fieldName="npi"
                      rules={{
                        min: {
                          value: 1000000000,
                          message: UserFormErrors.InvalidNpi
                        },
                        max: {
                          value: 2999999999,
                          message: UserFormErrors.InvalidNpi
                        },
                        pattern: {
                          value: Regex.Numeric,
                          message: UserFormErrors.InvalidNpi
                        }
                      }}
                      label="NPI"
                      disabled={anyApiIsLoading || editingEmail || !editingUser}
                    />
                    { errors.npi && (
                      <Alert severity="error">
                        {t(errors.npi.message || "NPI Error")}
                      </Alert>
                    )}
                  </Grid>
                  <Grid xs={12} md={6}>
                    <ReactHookFormTextField
                      control={control}
                      fieldName="email"
                      rules={{
                        required: UserFormErrors.EmptyEmail,
                        pattern: {
                          value: Regex.Email,
                          message: UserFormErrors.InvalidEmail
                        }
                      }}
                      label="Email Address"
                      required={editingUser}
                      disabled={
                        !editingEmail || updateEmailStatus === "loading"
                      }
                    />
                    {errors.email && (
                      <Alert severity="error">
                        {t(errors.email.message || "")}
                      </Alert>
                    )}
                  </Grid>

                  {editingUser && (
                    <Grid sx={{ pt: 0, alignItems: "center", display: "flex" }}>
                      {!editingEmail && (
                        <Button
                          variant="contained"
                          disabled={anyApiIsLoading || hasNonEmailDirty}
                          onClick={() => {
                            setEditingEmail(true);
                          }}
                        >
                          {t("Edit Email")}
                        </Button>
                      )}
                      {editingEmail && (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <LoadingButton
                            type="submit"
                            variant="contained"
                            loading={updateEmailStatus === "loading"}
                          >
                            {t("Save")}
                          </LoadingButton>
                          <Button
                            disabled={updateEmailStatus === "loading"}
                            variant="outlined"
                            onClick={() => {
                              // Revert only the email field and clear its errors; do not reset the whole form
                              setValue("email", user?.email ?? null, {
                                shouldDirty: false,
                                shouldValidate: false
                              });
                              clearErrors("email");
                              setEditingEmail(false);
                            }}
                          >
                            {t("Cancel")}
                          </Button>
                        </Box>
                      )}
                    </Grid>
                  )}

                  <Grid xs={12} md={6}>
                    <ReactHookFormTextField
                      control={control}
                      fieldName="simplLearnId"
                      label="SIMPL LearnID"
                      disabled={anyApiIsLoading || editingEmail || !editingUser}
                    />
                  </Grid>

                  <Grid xs={12} md={6}>
                    <ReactHookFormSelectField
                      control={control}
                      fieldName="pgy"
                      label="PGY"
                      disabled={anyApiIsLoading || editingEmail || !editingUser}
                      choices={PGYSelectFields}
                    />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <TextField
                      label={t("Status")}
                      value={userStatus}
                      sx={{ width: "100%" }}
                      disabled
                    />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <TextField
                      label={t("Registration Date")}
                      value={registrationDate}
                      sx={{ width: "100%" }}
                      disabled
                    />
                  </Grid>
                  {(getValues("programMemberships") || []).some((m) => {
                    const role = m.programRole as NewProgramRole;
                    return (
                      role === "Clinician" ||
                      role === ProgramRole.Trainee ||
                      role === ProgramRole.Attending
                    );
                  }) && (
                    <Grid xs={12} md={6}>
                      <ReactHookFormTextField
                        control={control}
                        fieldName="jobTitle"
                        rules={{ required: UserFormErrors.JobTitleRequired }}
                        label="Job Title"
                        required={editingUser}
                        disabled={
                          anyApiIsLoading || editingEmail || !editingUser
                        }
                      />
                      {errors.jobTitle && (
                        <Alert severity="error">
                          {t(UserFormErrors.JobTitleRequired)}
                        </Alert>
                      )}
                    </Grid>
                  )}
                </Grid>
              </fieldset>
            </Grid>
            <Grid xs={12}>
              <fieldset>
                <legend>{t("User Memberships")}</legend>
                <Grid xs={12} container p={1} spacing={2}>
                  <Grid xs={12}>
                    <List aria-label="memberships" sx={{ py: 0 }}>
                      <Grid xs={12} container p={1} spacing={2} px={0}>
                        {(getValues("programMemberships") || [])
                          .map((membershipField, originalIndex) => ({
                            membershipField,
                            originalIndex
                          }))
                          .sort(
                            (a, b) =>
                              new Date(
                                a.membershipField.startDate || 0
                              ).valueOf() -
                              new Date(
                                b.membershipField.startDate || 0
                              ).valueOf()
                          )
                          .map(({ membershipField, originalIndex }) => {
                            const formIndex = originalIndex;
                            const membershipProgram = programs?.find(
                              (p) => p.id == membershipField.programId
                            );
                            const tempKey = membershipField.id
                              ? membershipField.id
                              : generateUUID();

                            return (
                              <Grid xs={12} key={tempKey}>
                                <ListItem
                                  disablePadding
                                  sx={{
                                    flexDirection: "column",
                                    alignItems: "flex-start"
                                    // backgroundColor: "blue"
                                  }}
                                >
                                  <Grid container xs={12}>
                                    <Grid xs={12} md={4} sx={{ width: "100%" }}>
                                      <ReactHookFormSelectField
                                        control={control}
                                        fieldName={`programMemberships.${formIndex}.institutionId`}
                                        label="Institution"
                                        disabled={
                                          anyApiIsLoading ||
                                          editingEmail ||
                                          !membershipField.isNew
                                        }
                                        choices={
                                          membershipField.isNew
                                            ? institutionsCurrentUserCanAdminister?.flatMap(
                                                (institution) => {
                                                  const option: SelectMenuOption =
                                                    {
                                                      value:
                                                        institution?.id ?? "",
                                                      label:
                                                        institution?.name ?? ""
                                                    };
                                                  return option ? [option] : [];
                                                }
                                              ) ?? []
                                            : [
                                                {
                                                  value:
                                                    membershipProgram
                                                      ?.institution?.id ?? "",
                                                  label:
                                                    membershipProgram
                                                      ?.institution?.name ?? ""
                                                }
                                              ]
                                        }
                                        trigger={trigger}
                                      />
                                    </Grid>
                                    <Grid xs={12} md={4} sx={{ width: "100%" }}>
                                      <ReactHookFormSelectField
                                        control={control}
                                        fieldName={`programMemberships.${formIndex}.programId`}
                                        label="Program"
                                        disabled={
                                          anyApiIsLoading ||
                                          editingEmail ||
                                          !membershipField.isNew
                                        }
                                        required={membershipField.isNew}
                                        choices={
                                          membershipField.isNew
                                            ? getProgramsInInstitution(
                                                getValues(
                                                  `programMemberships.${formIndex}.institutionId`
                                                )
                                              )?.flatMap((program) => {
                                                const option: SelectMenuOption =
                                                  {
                                                    value: program.id,
                                                    label: program.name
                                                  };
                                                return option ? [option] : [];
                                              }) || []
                                            : [
                                                {
                                                  value:
                                                    membershipField.programId ??
                                                    "",
                                                  label:
                                                    membershipProgram?.name ??
                                                    ""
                                                }
                                              ]
                                        }
                                        trigger={trigger}
                                      />
                                    </Grid>
                                    <Grid xs={12} md={4} sx={{ width: "100%" }}>
                                      <ReactHookFormSelectField
                                        control={control}
                                        fieldName={`programMemberships.${formIndex}.programRole`}
                                        label="Role"
                                        disabled={
                                          anyApiIsLoading ||
                                          editingEmail ||
                                          !membershipField.isNew
                                        }
                                        // What is this doing? Unsure.
                                        // defaultValue={membershipField.programRole}
                                        required={membershipField.isNew}
                                        choices={programRoleSelectFields}
                                        trigger={trigger}
                                      />
                                    </Grid>

                                    {(getValues(
                                      `programMemberships.${formIndex}.programRole`
                                    ) as NewProgramRole) === "Clinician" && (
                                      <Grid
                                        xs={12}
                                        md={4}
                                        sx={{ width: "100%" }}
                                      >
                                        <Controller
                                          control={control}
                                          name={`programMemberships.${formIndex}.isClinicianTrainee`}
                                          rules={{
                                            validate: (value) =>
                                              (value !== null &&
                                                value !== undefined) ||
                                              "This field is required."
                                          }}
                                          render={({
                                            field: { onChange, value },
                                            fieldState: { error }
                                          }) => {
                                            const isDisabled =
                                              anyApiIsLoading ||
                                              editingEmail ||
                                              !membershipField.isNew;
                                            return (
                                              <FormControl
                                                required
                                                error={!!error}
                                                sx={{
                                                  width: "100%"
                                                }}
                                              >
                                                <FormLabel component="legend">
                                                  {t(
                                                    "Is this user a trainee in your program about whom you would like to collect assessments?"
                                                  )}
                                                </FormLabel>
                                                <RadioGroup
                                                  row
                                                  value={
                                                    value === true
                                                      ? "yes"
                                                      : value === false
                                                      ? "no"
                                                      : ""
                                                  }
                                                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                    const selected =
                                                      event.target.value === "yes";
                                                    onChange(selected);
                                                    trigger(
                                                      "programMemberships"
                                                    );
                                                  }}
                                                  sx={{ ml: 1 }}
                                                >
                                                  <FormControlLabel
                                                    value="yes"
                                                    control={
                                                      <Radio
                                                        disabled={isDisabled}
                                                      />
                                                    }
                                                    label={t("Yes")}
                                                  />
                                                  <FormControlLabel
                                                    value="no"
                                                    control={
                                                      <Radio
                                                        disabled={isDisabled}
                                                      />
                                                    }
                                                    label={t("No")}
                                                  />
                                                </RadioGroup>
                                                {error && (
                                                  <FormHelperText>
                                                    {error.message}
                                                  </FormHelperText>
                                                )}
                                              </FormControl>
                                            );
                                          }}
                                        />
                                      </Grid>
                                    )}
                                    {(getValues(
                                      `programMemberships.${formIndex}.programRole`
                                    ) as NewProgramRole) === "Clinician" &&
                                      getValues(
                                        `programMemberships.${formIndex}.isClinicianTrainee`
                                      ) && (
                                        <Grid
                                          xs={12}
                                          md={4}
                                          sx={{ width: "100%" }}
                                        >
                                          <ReactHookFormSelectField
                                            control={control}
                                            fieldName={`programMemberships.${formIndex}.traineeType`}
                                            label="Trainee Type"
                                            disabled={
                                              anyApiIsLoading ||
                                              editingEmail ||
                                              !membershipField.isNew
                                            }
                                            required={membershipField.isNew}
                                            choices={TraineeTypeSelectFields}
                                            trigger={trigger}
                                          />
                                        </Grid>
                                      )}
                                    <Grid xs={12} md={4}>
                                      <TextField
                                        sx={{ width: "100%" }}
                                        id="membership-start-date"
                                        label="Start Date"
                                        disabled
                                        value={
                                          membershipField.startDate
                                            ? format(
                                                new Date(
                                                  membershipField.startDate
                                                ),
                                                `PPPP`
                                              )
                                            : "N/A"
                                        }
                                      />
                                    </Grid>
                                    <Grid xs={12} md={4}>
                                      <TextField
                                        sx={{ width: "100%" }}
                                        id="membership-end-date"
                                        label="End Date"
                                        disabled
                                        value={
                                          membershipField.endDate
                                            ? format(
                                                new Date(
                                                  membershipField.endDate
                                                ),
                                                `PPPP`
                                              )
                                            : "N/A (Active)"
                                        }
                                      />
                                    </Grid>
                                    {editingUser &&
                                      (membershipField.isNew ||
                                        programsCurrentUserCanAdminister?.find(
                                          (p) =>
                                            p.id == membershipField.programId
                                        )) &&
                                      !membershipField.endDate && (
                                        <Grid
                                          xs={12}
                                          md={4}
                                          flex={1}
                                          sx={{ width: "100%" }}
                                        >
                                          <Button
                                            variant="outlined"
                                            startIcon={<DeleteOutlinedIcon />}
                                            disabled={
                                              anyApiIsLoading ||
                                              editingEmail ||
                                              !user.eulaAcceptedDate
                                            }
                                            onClick={() =>
                                              membershipField.isNew
                                                ? removeMembership(formIndex)
                                                : promptEndMembership(formIndex)
                                            }
                                            sx={{
                                              width: "100%",
                                              height: "100%"
                                            }}
                                          >
                                            {t(
                                              membershipField.isNew
                                                ? "Delete Membership"
                                                : "End Program Membership"
                                            )}
                                          </Button>
                                        </Grid>
                                      )}
                                  </Grid>
                                </ListItem>
                              </Grid>
                            );
                            // Dev Note: Only existing memberships will have an ID.
                          })}
                      </Grid>
                    </List>
                  </Grid>
                  {updateMembershipsError && (
                    <Grid>
                      <Alert severity="error">
                        {t(updateMembershipsError)}
                      </Alert>
                    </Grid>
                  )}
                  {errors.programMemberships && (
                    <Grid>
                      <Alert severity="error">
                        {t(
                          errors.programMemberships.root?.message ||
                            "Program Membership Error"
                        )}
                      </Alert>
                    </Grid>
                  )}
                  {editingUser && (
                    <Grid xs={12}>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        disabled={
                          editingEmail ||
                          anyApiIsLoading ||
                          // TODO: Make this based on logged-in user's ProgramMemberships
                          // Disable the button if there aren't any
                          // programs in the user's institution
                          !programsCurrentUserCanAdminister ||
                          programsCurrentUserCanAdminister.length < 1 ||
                          !user.eulaAcceptedDate
                        }
                        onClick={() => {
                          if (id) {
                            const institutionId =
                              institutionsCurrentUserCanAdminister?.length == 1
                                ? institutionsCurrentUserCanAdminister[0]?.id
                                : "";
                            const programs =
                              getProgramsInInstitution(institutionId);
                            const programId =
                              programs?.length == 1 ? programs[0]?.id : "";
                            const newMembership: MembershipPreview = {
                              id: generateUUID(),
                              __typename: "ProgramMembership",
                              programMemberId: id,
                              programRole: membershipType,
                              institutionId,
                              programId,
                              isNew: true,
                              startDate: new Date().toISOString()
                            };
                            appendMembership(newMembership);
                          }
                        }}
                      >
                        {t("Add Membership")}
                      </Button>
                    </Grid>
                  )}
                  {editingUser && !user.eulaAcceptedDate && (
                    <Grid xs={12}>
                      <Alert severity="info">
                        {t(
                          "An unregistered user's program membership cannot be edited."
                        )}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </fieldset>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            p={1}
            sx={{ backgroundColor: "background.paper", zIndex: 1 }}
          >
            {!editingUser && (
              <Grid>
                <Button
                  disabled={anyApiIsLoading || editingEmail}
                  onClick={() => setEditingUser(true)}
                  variant="contained"
                >
                  {t("Edit User")}
                </Button>
              </Grid>
            )}
            {editingUser && (
              <>
                <Grid>
                  <LoadingButton
                    disabled={
                      !isDirty ||
                      anyApiIsLoading ||
                      editingEmail ||
                      !!errors.programMemberships
                    }
                    loading={
                      updateUserStatus === "loading" ||
                      updateMembershipsStatus === "loading"
                    }
                    type="submit"
                    variant="contained"
                  >
                    {t("Save")}
                  </LoadingButton>
                </Grid>
                <Grid>
                  <Button
                    onClick={() => {
                      setEditingUser(false);
                      setFormToStoredValues(user);
                    }}
                    disabled={anyApiIsLoading}
                    variant="outlined"
                  >
                    {t("Cancel")}
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </form>
      </PageWrapper>
    );
  }
  // Null return to satisfy typescript requirements
  return null;
};
